import React, { useMemo } from 'react';
import { useRouter } from 'next/router';

import { useQuery, QueryClient, dehydrate } from 'react-query';
import { Pagination } from '@mui/material';

import { GetExperts } from 'services/experts';
import { queryClientConfig } from '@core/configs/queryClientConfig';
import { setSsrTokens } from 'utils/authentication';

import { getExpertsProps } from 'router/seoprops';
import { NextSeo } from 'next-seo';

import ExpertCard from '@core/components/expertcard/ExpertCard';
import ExpertsHeader from 'components/pages/experts/expertsheader/ExpertsHeader';
import Spinner from '@core/components/spinner/Spinner';

import classes from './experts.module.scss';

const PAGE_SIZE = 12;

const buildQueryParams = (query) => {
    const params = { page_size: PAGE_SIZE };
    if (query?.page) params.page = parseInt(query.page, 10) || 1;
    return params;
};

const Experts = () => {
    const router = useRouter();
    const seoProps = getExpertsProps();

    const filters = useMemo(() => buildQueryParams(router.query), [router.query]);
    const currentPage = filters.page || 1;

    const { data, isLoading, isPreviousData } = useQuery(
        ['experts', filters],
        () => GetExperts(filters),
        { keepPreviousData: true }
    );

    const experts = data?.data?.results || [];
    const totalCount = data?.data?.count || 0;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    const handlePageChange = (_event, page) => {
        const newQuery = { ...router.query, page };
        // Page 1 carries no param so /experts stays the canonical URL.
        if (page === 1) delete newQuery.page;
        router.replace({ pathname: '/experts', query: newQuery }, undefined, { shallow: true });
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
    };

    return (
        <>
            <NextSeo {...seoProps} />
            {/* The desktop layout centres page children in a flex row, so the
                page has to be a single full-width root or its sections would sit
                side by side. */}
            <div className={classes['page']}>
                <div className='container px-xl-0'>
                    <ExpertsHeader />
                </div>
                <div className={classes['band']}>
                    <div className='container px-xl-0'>
                        <div className='row d-flex justify-content-center'>
                            <div className='col-xl-10 col-12'>
                                <div className={classes['wrapper']}>
                                    {isLoading ? (
                                        <div className={classes['loading']}>
                                            <Spinner />
                                        </div>
                                    ) : (
                                        <>
                                            {/* keepPreviousData holds the outgoing page
                                                in place while the next one loads; dimming
                                                it signals the change without a jump. */}
                                            <div
                                                className={`${classes['card-grid']} ${
                                                    isPreviousData ? classes['is-fetching'] : ''
                                                }`}>
                                                {experts.length > 0 ? (
                                                    experts.map((expert) => (
                                                        <ExpertCard
                                                            key={expert.unique_string_id}
                                                            expert={expert}
                                                        />
                                                    ))
                                                ) : (
                                                    <div className={classes['empty']}>
                                                        No experts to show yet.
                                                    </div>
                                                )}
                                            </div>

                                            {totalPages > 1 && (
                                                <div className={classes['pagination']}>
                                                    <Pagination
                                                        count={totalPages}
                                                        page={currentPage}
                                                        onChange={handlePageChange}
                                                        variant='outlined'
                                                        shape='rounded'
                                                        color='primary'
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export async function getServerSideProps(context) {
    setSsrTokens(context);
    const queryClient = new QueryClient(queryClientConfig);
    const filters = buildQueryParams(context.query);

    try {
        const response = await GetExperts(filters);
        await queryClient.prefetchQuery(['experts', filters], () => ({ data: response.data }));
    } catch (e) {
        // The API 404s an out-of-range page. Without this the page would render
        // a spinner that never resolves, so send those URLs back to page one.
        if (e?.response?.status === 404) {
            return { redirect: { permanent: false, destination: '/experts' } };
        }
        // Anything else: fall through and let the client retry.
    }

    return {
        props: {
            dehydratedState: dehydrate(queryClient)
        }
    };
}

export default Experts;
